import React, { useState, useEffect } from 'react';
import { ArrowRight, Mail, Bell, Gift, Crown, Target, TrendingUp, Eye, Leaf, Recycle, Heart, ShoppingCart, Package, Flame, Droplets, Star, Check, Sparkles, Users, Award, ChevronDown, Clock, Shield, Zap, ArrowLeft, Brain, Menu, X } from 'lucide-react';
import WaitlistForm from './WaitlistForm';
import WaitlistStats from './WaitlistStats';

interface WaitlistPageProps {
  onEnterApp: () => void;
  onShowPrivacy: () => void;
  onShowTerms: () => void;
  onShowOurStory: () => void;
  onGoToShop?: () => void;
}

export default function WaitlistPage({ onEnterApp, onShowPrivacy, onShowTerms, onShowOurStory, onGoToShop }: WaitlistPageProps) {
  const [currentHero, setCurrentHero] = useState(0);
  const [currentFlavor, setCurrentFlavor] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentWord, setCurrentWord] = useState(0); // For Strip • Drip • Glow animation

  // Flavor data for the homepage display
  const flavors = [
    { name: 'Deep Violet', color: 'from-purple-600 to-purple-800', description: 'Smoky chipotle with dark chocolate' },
    { name: 'Electric Cobalt', color: 'from-blue-500 to-blue-700', description: 'Tangy blueberry habanero fusion' },
    { name: 'Lime Zest', color: 'from-green-400 to-green-600', description: 'Fresh cilantro jalapeño kick' },
    { name: 'Solar Mango', color: 'from-yellow-400 to-orange-500', description: 'Tropical heat with ginger notes' },
    { name: 'Crimson Rush', color: 'from-red-500 to-red-700', description: 'Ghost pepper meets cherry fire' },
    { name: 'Charcoal Black', color: 'from-gray-700 to-gray-900', description: 'Activated charcoal umami depth' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHero((prev) => (prev + 1) % 8);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const flavorInterval = setInterval(() => {
      setCurrentFlavor((prev) => (prev + 1) % flavors.length);
    }, 3000);
    return () => clearInterval(flavorInterval);
  }, [flavors.length]);

  // Strip • Drip • Glow word cycling animation
  useEffect(() => {
    const wordInterval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % 3);
    }, 2000); // Change word every 2 seconds
    return () => clearInterval(wordInterval);
  }, []);

  const handleSmoothScroll = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleExploreShop = () => {
    console.log('Explore Sauces clicked - navigating to shop');
    if (onGoToShop) {
      onGoToShop();
    } else {
      console.error('onGoToShop function not provided');
    }
    setIsMenuOpen(false);
  };

  // Enhanced hero images with all new photoshoot assets
  const heroImages = [
    {
      src: '/photoshoot/undressing shoot .png',
      title: 'Future Vision',
      subtitle: 'Purple power unleashed',
      type: 'girl'
    },
    {
      src: '/photoshoot/undressing guy 1.png',
      title: 'Bold Energy',
      subtitle: 'Unstoppable flavor',
      type: 'guy'
    },
    {
      src: '/photoshoot/undressing guy 3.png',
      title: 'Action Ready',
      subtitle: 'Squeeze the moment',
      type: 'guy'
    },
    {
      src: '/photoshoot/undressing shoot 2.png',
      title: 'Flavor Collection',
      subtitle: 'Six bold varieties',
      type: 'girl'
    },
    {
      src: '/photoshoot/undressing sampler box.png',
      title: 'Complete Collection',
      subtitle: 'Six flavors, endless possibilities',
      type: 'product'
    },
    {
      src: '/photoshoot/undressing shoot 1.png',
      title: 'Tech Meets Taste',
      subtitle: 'Innovation in every drop',
      type: 'girl'
    },
    {
      src: '/photoshoot/undressing guy 3.png',
      title: 'Drip & Glow',
      subtitle: 'Pure sauce perfection',
      type: 'guy'
    },
    {
      src: '/photoshoot/undressing shoot 4.png',
      title: 'Multi-Flavor',
      subtitle: 'Endless possibilities',
      type: 'girl'
    }
  ];

  // Get the current word styling for Strip • Drip • Glow
  const getWordStyling = (wordIndex: number) => {
    if (wordIndex === currentWord) {
      // Active word gets neon glow effect
      switch (wordIndex) {
        case 0: // Strip
          return 'text-pink-400 animate-pulse drop-shadow-[0_0_15px_rgba(236,72,153,0.8)]';
        case 1: // Drip
          return 'text-cyan-400 animate-pulse drop-shadow-[0_0_15px_rgba(6,182,212,0.8)]';
        case 2: // Glow
          return 'text-yellow-400 animate-pulse drop-shadow-[0_0_15px_rgba(250,204,21,0.8)]';
        default:
          return 'text-white';
      }
    }
    return 'text-white/70'; // Inactive words are dimmed
  };

  return (
    <>
      {/* Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Space+Grotesk:wght@400;500;600;700&family=Poppins:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
      
      <style jsx>{`
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-space { font-family: 'Space Grotesk', sans-serif; }
        .font-poppins { font-family: 'Poppins', sans-serif; }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            background: linear-gradient(135deg, #6366f1, #ec4899);
            box-shadow: 0 0 20px rgba(236, 72, 153, 0.4);
          }
          50% { 
            background: linear-gradient(135deg, #ec4899, #06b6d4);
            box-shadow: 0 0 30px rgba(236, 72, 153, 0.6);
          }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes heroTransition {
          0% { opacity: 0; transform: scale(1.05); }
          10% { opacity: 1; transform: scale(1); }
          90% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.95); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes morphBackground {
          0%, 100% { 
            background: linear-gradient(135deg, #1e1b4b, #581c87, #be185d);
          }
          33% { 
            background: linear-gradient(135deg, #581c87, #be185d, #0c4a6e);
          }
          66% { 
            background: linear-gradient(135deg, #be185d, #0c4a6e, #1e1b4b);
          }
        }
        
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 4s ease-in-out infinite; }
        .animate-slide-up { animation: slideUp 0.6s ease-out forwards; }
        .animate-morph { animation: morphBackground 8s ease-in-out infinite; }
        
        .glass-morphism {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .glass-ultra {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.06);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hover-lift:hover {
          transform: translateY(-4px);
        }
        
        .hero-image {
          filter: contrast(1.1) saturate(1.2) brightness(1.05);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .hero-overlay {
          background: linear-gradient(45deg, rgba(99, 102, 241, 0.2), rgba(236, 72, 153, 0.25));
          mix-blend-mode: overlay;
        }
        
        .hero-guy {
          filter: contrast(1.15) saturate(1.3) brightness(1.0) hue-rotate(-3deg);
        }
        
        .hero-girl {
          filter: contrast(1.05) saturate(1.15) brightness(1.1) hue-rotate(3deg);
        }
        
        .hero-product {
          filter: contrast(1.05) saturate(1.05) brightness(1.05);
        }
        
        .hero-indicator {
          transition: all 0.3s ease;
        }

        .hero-indicator.guy {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          box-shadow: 0 0 15px rgba(59, 130, 246, 0.4);
        }

        .hero-indicator.girl {
          background: linear-gradient(135deg, #ec4899, #be185d);
          box-shadow: 0 0 15px rgba(236, 72, 153, 0.4);
        }
        
        .hero-indicator.product {
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          box-shadow: 0 0 15px rgba(139, 92, 246, 0.4);
        }
        
        .nav-link {
          position: relative;
          overflow: hidden;
        }
        
        .nav-link::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s;
        }
        
        .nav-link:hover::before {
          left: 100%;
        }
        
        .premium-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.25);
        }
        
        .premium-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
        }
        
        .shimmer-effect {
          position: relative;
          overflow: hidden;
        }
        
        .shimmer-effect::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          animation: shimmer 2s infinite;
        }

        .text-readability-gradient {
          background: linear-gradient(to right, 
            rgba(0, 0, 0, 0.7) 0%, 
            rgba(0, 0, 0, 0.5) 30%, 
            rgba(0, 0, 0, 0.2) 60%, 
            transparent 100%
          );
        }

        .mobile-menu-slide {
          transform: translateY(-100%);
          transition: transform 0.3s ease-in-out;
        }
        
        .mobile-menu-slide.open {
          transform: translateY(0);
        }

        /* Mobile Responsive Improvements */
        @media (max-width: 768px) {
          .glass-morphism, .glass-ultra, .premium-card {
            backdrop-filter: blur(12px);
          }
        }
        
        /* Ensure mobile menu is always visible when open */
        .mobile-menu-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 60;
        }

        /* Enhanced neon glow effects for Strip Drip Glow */
        .neon-strip {
          color: #ec4899;
          text-shadow: 
            0 0 5px rgba(236, 72, 153, 0.8),
            0 0 10px rgba(236, 72, 153, 0.6),
            0 0 15px rgba(236, 72, 153, 0.4),
            0 0 20px rgba(236, 72, 153, 0.2);
        }

        .neon-drip {
          color: #06b6d4;
          text-shadow: 
            0 0 5px rgba(6, 182, 212, 0.8),
            0 0 10px rgba(6, 182, 212, 0.6),
            0 0 15px rgba(6, 182, 212, 0.4),
            0 0 20px rgba(6, 182, 212, 0.2);
        }

        .neon-glow {
          color: #fbbf24;
          text-shadow: 
            0 0 5px rgba(251, 191, 36, 0.8),
            0 0 10px rgba(251, 191, 36, 0.6),
            0 0 15px rgba(251, 191, 36, 0.4),
            0 0 20px rgba(251, 191, 36, 0.2);
        }
      `}</style>

      <div className="min-h-screen animate-morph relative overflow-hidden">
        {/* IMPROVED MOBILE-FIRST HEADER */}
        <header className="fixed top-0 left-0 right-0 z-50 glass-ultra border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 sm:h-20">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logo transparent with tagline.png" 
                  alt="UNDRESSING™ Sauce Logo" 
                  className="h-10 sm:h-12 lg:h-16 w-auto object-contain"
                />
              </div>
              
              {/* Desktop Navigation - Hidden on mobile/tablet */}
              <nav className="hidden xl:flex items-center space-x-6 2xl:space-x-8">
                <button 
                  onClick={() => handleSmoothScroll('flavors')}
                  className="nav-link text-white hover:text-pink-300 transition-all duration-300 font-medium relative group"
                >
                  <span className="relative z-10">Flavors</span>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-600 group-hover:w-full transition-all duration-300"></div>
                </button>
                <button 
                  onClick={() => handleSmoothScroll('process')}
                  className="nav-link text-white hover:text-pink-300 transition-all duration-300 font-medium relative group"
                >
                  <span className="relative z-10">Process</span>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-600 group-hover:w-full transition-all duration-300"></div>
                </button>
                <button 
                  onClick={onShowOurStory}
                  className="nav-link text-white hover:text-pink-300 transition-all duration-300 font-medium relative group"
                >
                  <span className="relative z-10">Our Story</span>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-600 group-hover:w-full transition-all duration-300"></div>
                </button>
                <button 
                  onClick={() => handleSmoothScroll('waitlist')}
                  className="nav-link text-white hover:text-pink-300 transition-all duration-300 font-medium relative group"
                >
                  <span className="relative z-10">Join Waitlist</span>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-600 group-hover:w-full transition-all duration-300"></div>
                </button>
                <button
                  onClick={onEnterApp}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2 shimmer-effect"
                >
                  <Brain className="h-4 w-4" />
                  <span>DrizzID™ App</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </nav>

              {/* Mobile/Tablet Navigation */}
              <div className="xl:hidden flex items-center space-x-3">
                <button
                  onClick={onEnterApp}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-3 sm:px-4 py-2 rounded-full font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 flex items-center space-x-2 text-sm sm:text-base"
                >
                  <Brain className="h-4 w-4" />
                  <span className="hidden sm:inline">DrizzID™</span>
                  <span className="sm:hidden">App</span>
                </button>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* IMPROVED MOBILE MENU - Dropdown style */}
          {isMenuOpen && (
            <div className="mobile-menu-container">
              <div className={`xl:hidden bg-gray-900/95 backdrop-blur-md border-b border-white/10 mobile-menu-slide ${isMenuOpen ? 'open' : ''}`}>
                <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
                  <button 
                    onClick={() => handleSmoothScroll('flavors')}
                    className="block w-full text-left text-white hover:text-pink-300 transition-colors font-medium py-3 px-4 rounded-lg hover:bg-white/10"
                  >
                    Flavors
                  </button>
                  <button 
                    onClick={() => handleSmoothScroll('process')}
                    className="block w-full text-left text-white hover:text-pink-300 transition-colors font-medium py-3 px-4 rounded-lg hover:bg-white/10"
                  >
                    Process
                  </button>
                  <button 
                    onClick={onShowOurStory}
                    className="block w-full text-left text-white hover:text-pink-300 transition-colors font-medium py-3 px-4 rounded-lg hover:bg-white/10"
                  >
                    Our Story
                  </button>
                  <button 
                    onClick={() => handleSmoothScroll('waitlist')}
                    className="block w-full text-left text-white hover:text-pink-300 transition-colors font-medium py-3 px-4 rounded-lg hover:bg-white/10"
                  >
                    Join Waitlist
                  </button>
                  <div className="pt-4 border-t border-gray-700">
                    <button
                      onClick={onEnterApp}
                      className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <Brain className="h-4 w-4" />
                      <span>Launch DrizzID™ App</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </header>

        {/* HERO SECTION - Added top padding for fixed header */}
        <section className="min-h-screen flex items-center relative overflow-hidden pt-16 sm:pt-20">
          {/* Enhanced Hero Image Carousel */}
          <div className="absolute inset-0">
            {heroImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-1000 ${
                  index === currentHero ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                }`}
              >
                <img 
                  src={image.src}
                  alt={image.title}
                  className={`w-full h-full object-cover hero-image ${
                    image.type === 'guy' ? 'hero-guy' : 
                    image.type === 'girl' ? 'hero-girl' : 'hero-product'
                  }`}
                />
                <div className="hero-overlay absolute inset-0"></div>
                <div className={`absolute inset-0 ${
                  image.type === 'guy' 
                    ? 'bg-gradient-to-r from-blue-900/50 via-transparent to-purple-900/30' 
                    : image.type === 'girl'
                    ? 'bg-gradient-to-r from-pink-900/50 via-transparent to-purple-900/30'
                    : 'bg-gradient-to-r from-purple-900/50 via-transparent to-indigo-900/30'
                }`}></div>
                <div className="absolute inset-0 text-readability-gradient"></div>
              </div>
            ))}
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Column */}
            <div className="text-left space-y-6 sm:space-y-8 animate-slide-up">
              <div className="inline-flex items-center space-x-2 sm:space-x-3 premium-card rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-4">
                <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-pink-400" />
                <span className="text-pink-300 font-medium text-sm sm:text-base">Revolutionary Sauce Technology</span>
              </div>
              
              <h1 className="font-poppins font-black text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-tight text-white">
                Bold Flavors,
                <span className="gradient-text block">On the Go</span>
              </h1>
              
              <p className="font-inter text-lg sm:text-xl text-slate-200 leading-relaxed max-w-xl">
                Strip the bland, drip the bold, glow all day anywhere you eat. 
                Revolutionary sauce pouches designed for the modern lifestyle.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <button 
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 font-poppins font-semibold text-base sm:text-lg hover:shadow-xl hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-3 rounded-xl animate-pulse-glow group"
                  onClick={() => handleSmoothScroll('waitlist')}
                >
                  <Package className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span>Strip My Meal</span>
                  <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button 
                  onClick={handleExploreShop}
                  className="border-2 border-pink-500/50 text-pink-300 px-6 sm:px-8 py-3 sm:py-4 font-poppins font-semibold text-base sm:text-lg hover:bg-pink-500/10 transition-all duration-300 rounded-xl flex items-center justify-center space-x-3 group"
                >
                  <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span>Explore Sauces</span>
                </button>
              </div>

              {/* Enhanced Hero Image Indicators */}
              <div className="flex items-center space-x-4 sm:space-x-6 pt-6">
                <div className="flex space-x-2 sm:space-x-3">
                  {heroImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentHero(index)}
                      className={`hero-indicator w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${
                        index === currentHero 
                          ? `scale-125 ${image.type}` 
                          : 'bg-white/30 hover:bg-white/50'
                      }`}
                      title={`${image.type === 'guy' ? '👨' : image.type === 'girl' ? '👩' : '📦'} ${image.title}`}
                    />
                  ))}
                </div>
                <div className="text-white/90 font-medium flex items-center space-x-2 sm:space-x-3">
                  <span className={`text-lg sm:text-xl ${
                    heroImages[currentHero].type === 'guy' ? '👨' : 
                    heroImages[currentHero].type === 'girl' ? '👩' : '📦'
                  }`}></span>
                  <span className="hidden sm:inline text-sm sm:text-base">{heroImages[currentHero].title} • {heroImages[currentHero].subtitle}</span>
                  <span className="sm:hidden text-sm">{heroImages[currentHero].title}</span>
                </div>
              </div>

              {/* Waitlist Stats */}
              <div className="pt-4">
                <WaitlistStats />
              </div>
            </div>

            {/* Right Column - Logo Display */}
            <div className="relative flex justify-center animate-slide-up" style={{ animationDelay: '0.3s' }}>
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <img 
                    src="/logo transparent with tagline.png" 
                    alt="UNDRESSING™ Sauce Logo" 
                    className="h-64 sm:h-80 lg:h-96 w-auto object-contain animate-float"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY UNDRESSING™ SAUCE? */}
        <section className="py-16 sm:py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/30"></div>
          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="font-poppins font-black text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Why UNDRESSING™ Sauce?
              </h2>
              <p className="font-inter text-lg sm:text-xl text-slate-300">Four pillars of flavor revolution</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[
                {
                  emoji: '🔥',
                  title: 'Bold',
                  desc: 'High-intensity taste & colour',
                  color: 'from-red-500 to-orange-500'
                },
                {
                  emoji: '⚡',
                  title: 'Mobile',
                  desc: 'Carabiner eyelet + one-hand squeeze',
                  color: 'from-yellow-500 to-orange-500'
                },
                {
                  emoji: '🫶',
                  title: 'Wellness-Driven',
                  desc: '≤2g sugar, superfood boosters',
                  color: 'from-green-500 to-emerald-500'
                },
                {
                  emoji: '🌎',
                  title: 'Planet-Kind',
                  desc: 'Mono-material bio-PET, recyclable',
                  color: 'from-blue-500 to-cyan-500'
                }
              ].map((benefit, index) => (
                <div 
                  key={index}
                  className="premium-card rounded-2xl p-6 text-center hover-lift transition-all duration-300 group shimmer-effect"
                >
                  <div className="text-4xl sm:text-5xl mb-4">{benefit.emoji}</div>
                  <h3 className="font-poppins font-bold text-xl sm:text-2xl text-white mb-3">{benefit.title}</h3>
                  <p className="font-inter text-slate-300 leading-relaxed">{benefit.desc}</p>
                  <div className={`w-full h-2 bg-gradient-to-r ${benefit.color} rounded-full mt-4 opacity-60 group-hover:opacity-100 transition-opacity`}></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* STRIP • DRIP • GLOW™ Process - Updated with individual word color changes */}
        <section id="process" className="py-16 sm:py-20 relative process-section">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/30 to-transparent"></div>
          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="font-poppins font-black text-3xl sm:text-4xl lg:text-5xl mb-6">
                <span className={`transition-all duration-500 ${getWordStyling(0)}`}>Strip</span>
                <span className="text-white/50 mx-2 sm:mx-4">•</span>
                <span className={`transition-all duration-500 ${getWordStyling(1)}`}>Drip</span>
                <span className="text-white/50 mx-2 sm:mx-4">•</span>
                <span className={`transition-all duration-500 ${getWordStyling(2)}`}>Glow</span>
                <span className="text-white/70">™</span>
              </h2>
              <p className="font-inter text-lg sm:text-xl text-slate-300">Three steps to flavor transformation</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 sm:gap-16 items-center">
              {[
                {
                  icon: Package,
                  title: 'Strip',
                  desc: 'Peel the smart-tab to reveal pure flavor',
                  color: 'from-pink-500 to-rose-500',
                  image: '/photoshoot/undressing shoot 2.png'
                },
                {
                  icon: Droplets,
                  title: 'Drip',
                  desc: 'Squeeze the perfect amount every time',
                  color: 'from-cyan-500 to-blue-500',
                  image: '/photoshoot/undressing guy 3.png'
                },
                {
                  icon: Sparkles,
                  title: 'Glow',
                  desc: 'Experience bold flavors that energize',
                  color: 'from-yellow-500 to-orange-500',
                  image: '/photoshoot/undressing shoot 1.png'
                }
              ].map((step, index) => (
                <div key={index} className="text-center group">
                  <div className="relative mb-8">
                    <div className="process-step-container w-40 h-40 sm:w-48 sm:h-48 mx-auto rounded-2xl overflow-hidden mb-6 group-hover:scale-105 transition-transform duration-300 shadow-xl">
                      <img 
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                        style={{
                          filter: 'none',
                          WebkitFilter: 'none'
                        }}
                      />
                      <div className={`absolute inset-0 bg-gradient-to-r ${step.color} mix-blend-overlay opacity-30`}></div>
                    </div>
                    <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r ${step.color} rounded-full flex items-center justify-center mx-auto -mt-28 sm:-mt-32 relative z-10 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <step.icon className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                    </div>
                  </div>
                  <h3 className="font-poppins font-bold text-2xl sm:text-3xl text-white mb-4">{step.title}</h3>
                  <p className="font-inter text-slate-300 leading-relaxed max-w-sm mx-auto text-lg">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FLAVOR FLIGHT SAMPLER */}
        <section id="flavors" className="py-16 sm:py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/30"></div>
          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 items-center">
              {/* Left Column */}
              <div className="space-y-6 sm:space-y-8">
                <h2 className="font-poppins font-black text-3xl sm:text-4xl lg:text-5xl text-white">
                  Flavor Flight
                  <span className="gradient-text block">Sampler</span>
                </h2>
                
                <p className="font-inter text-lg sm:text-xl text-slate-300 leading-relaxed">
                  Six colour-pumped SKUs designed to awaken every taste bud. 
                  Each pouch delivers a unique flavor journey that transforms ordinary meals into extraordinary experiences.
                </p>
                
                <div className="space-y-4">
                  {flavors.map((flavor, index) => (
                    <div 
                      key={index}
                      className={`flex items-center space-x-4 sm:space-x-6 p-4 sm:p-6 premium-card rounded-xl transition-all duration-300 ${
                        index === currentFlavor ? 'bg-white/15 scale-105' : 'hover:bg-white/10'
                      }`}
                    >
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r ${flavor.color} rounded-full ${
                        index === currentFlavor ? 'animate-pulse' : ''
                      }`}></div>
                      <div>
                        <h4 className="font-poppins font-semibold text-white text-lg sm:text-xl">{flavor.name}</h4>
                        <p className="font-inter text-slate-400 text-sm sm:text-base">{flavor.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* EXPLORE SAUCES BUTTON LINKS TO SHOP */}
                <button 
                  className="border-2 border-pink-500 text-pink-400 hover:bg-pink-500/10 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 flex items-center space-x-3 group"
                  onClick={handleExploreShop}
                >
                  <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span>Explore Sauces</span>
                  <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Right Column */}
              <div className="relative">
                <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl">
                  <img 
                    src="/photoshoot/undressing guy 1.png"
                    alt="Multi-Flavor Collection"
                    className="w-full h-80 sm:h-96 object-cover hero-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/50 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/20">
                      <h3 className="font-poppins font-bold text-xl sm:text-2xl text-white mb-3">Six Bold Varieties</h3>
                      <p className="font-inter text-slate-300 text-sm sm:text-base mb-4">Endless flavor combinations at your fingertips</p>
                      
                      <div className="flex justify-center space-x-2">
                        {flavors.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentFlavor(index)}
                            className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${
                              index === currentFlavor ? 'bg-pink-500 scale-125' : 'bg-white/40'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 w-16 h-20 sm:w-20 sm:h-24 premium-card rounded-2xl p-4 animate-float" style={{ animationDelay: '1s' }}>
                  <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Package className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                </div>
                
                <div className="absolute -bottom-6 -left-6 w-16 h-20 sm:w-20 sm:h-24 premium-card rounded-2xl p-4 animate-float" style={{ animationDelay: '2s' }}>
                  <div className="w-full h-full bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <Droplets className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WAITLIST SECTION */}
        <section id="waitlist" className="py-16 sm:py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-900/20 to-transparent"></div>
          <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
            <div className="premium-card rounded-2xl sm:rounded-3xl p-8 sm:p-12 shimmer-effect">
              <div className="mb-8 sm:mb-12">
                <Crown className="h-12 w-12 sm:h-16 sm:w-16 text-yellow-400 mx-auto mb-6" />
                <h2 className="font-poppins font-black text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                  Unlock the Flavor
                  <span className="gradient-text block">Future</span>
                </h2>
                <p className="font-inter text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
                  Score first-access UNDRESSING™ Sauce drip pouches—limited drops, exclusive infusions, launch-only pricing.
                </p>
              </div>
              
              <div className="mb-8 sm:mb-12">
                <WaitlistForm 
                  source="main-waitlist" 
                  onSuccess={() => {
                    console.log('Waitlist signup completed!')
                  }}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-slate-400">
                <div className="flex flex-col items-center space-y-2">
                  <Check className="h-6 w-6 sm:h-7 sm:w-7 text-green-400" />
                  <span className="font-medium">Early access pricing</span>
                </div>
                <div className="flex flex-col items-center space-y-2">
                  <Check className="h-6 w-6 sm:h-7 sm:w-7 text-green-400" />
                  <span className="font-medium">Exclusive flavor previews</span>
                </div>
                <div className="flex flex-col items-center space-y-2">
                  <Check className="h-6 w-6 sm:h-7 sm:w-7 text-green-400" />
                  <span className="font-medium">Free shipping on launch</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER - ALL STATUSES SET TO "COMING SOON" */}
        <footer className="py-12 sm:py-16 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12 mb-8 sm:mb-12">
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <img 
                    src="/logo transparent with tagline.png" 
                    alt="UNDRESSING™ Sauce Logo" 
                    className="h-16 sm:h-20 w-auto object-contain"
                  />
                </div>
                <p className="font-poppins text-pink-400 font-bold tracking-wide text-lg">Strip. Drip. Glow™</p>
              </div>
              
              <div>
                <h4 className="font-poppins font-semibold text-white mb-6 text-xl">Products</h4>
                <ul className="space-y-3 text-slate-400">
                  <li className="flex items-center justify-between">
                    <button onClick={() => handleSmoothScroll('flavors')} className="hover:text-pink-300 transition-colors">Flavor Collection</button>
                    <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-semibold">Coming Soon</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <button onClick={handleExploreShop} className="hover:text-pink-300 transition-colors">Shop Sauces</button>
                    <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-semibold">Coming Soon</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <a href="#" className="hover:text-pink-300 transition-colors">Limited Editions</a>
                    <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-semibold">Coming Soon</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <a href="#" className="hover:text-pink-300 transition-colors">Bulk Orders</a>
                    <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-semibold">Coming Soon</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-poppins font-semibold text-white mb-6 text-xl">Company</h4>
                <ul className="space-y-3 text-slate-400">
                  <li><button onClick={onShowOurStory} className="hover:text-pink-300 transition-colors">Our Story</button></li>
                  <li><button onClick={onShowTerms} className="hover:text-pink-300 transition-colors">Terms & Conditions</button></li>
                  <li><button onClick={onShowPrivacy} className="hover:text-pink-300 transition-colors">Privacy Policy</button></li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-white/10 pt-8 text-center">
              <p className="font-inter text-slate-500">
                © 2025 UNDRESSING™ Sauce. All rights reserved. Bold flavors, on the go.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}